import { useEffect, useState } from "react";
import { DeviceMobileIcon, XIcon, ShareNetworkIcon } from "@phosphor-icons/react";

const DISMISS_KEY = "cs.pwa_install_dismissed_at";
const REPROMPT_DAYS = 30;

/**
 * Lightweight in-app prompt for adding CARGO/SCAN to the iOS home screen.
 * Android Chrome shows its own native install prompt — we only nudge iOS Safari
 * (the only major browser with no built-in PWA install flow).
 */
export default function PWAInstallBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Already installed (running standalone)?
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone) return; // iOS legacy flag
    // Only iOS Safari needs the manual instructions
    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isSafari = /^((?!chrome|crios|fxios|edgios).)*safari/i.test(ua);
    if (!isIOS || !isSafari) return;
    // Respect previous dismiss
    const dismissed = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissed && Date.now() - dismissed < REPROMPT_DAYS * 86400 * 1000) return;
    // Show with small delay so it feels less aggressive
    const t = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="lg:hidden fixed bottom-[68px] inset-x-3 z-40 border border-zinc-950 bg-white shadow-lg p-4"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 bg-zinc-950 text-white flex items-center justify-center flex-shrink-0">
          <DeviceMobileIcon size={20} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-sm tracking-tight mb-0.5">
            Install CARGO/SCAN
          </div>
          <div className="text-xs text-zinc-600 leading-snug">
            Tap{" "}
            <ShareNetworkIcon size={13} weight="bold" className="inline mb-0.5" /> Share, then{" "}
            <strong>Add to Home Screen</strong> for full-screen + push notifications.
          </div>
        </div>
        <button
          data-testid="pwa-banner-dismiss"
          onClick={dismiss}
          className="p-1 text-zinc-400 hover:text-zinc-950 -mr-1 -mt-1"
          aria-label="Dismiss"
        >
          <XIcon size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
